import React, { useState, useRef, useEffect } from 'react';
import ReactPlayer from 'react-player';
import BlurGradientWrapper from '../components/BlurGradientWrapper.js';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../App.css';
import ScrollToSectionButton from '../components/ScrollToSectionButton';
import NewsletterForm from '../components/NewsletterForm'; 

import {PlayCircle, ChevronRight, Check } from 'lucide-react';

// Define consistent heights as constants
const SECTION_HEIGHTS = {
  hero: 'min-h-screen',
  standard: 'min-h-[600px]',
  extended: 'min-h-[700px]',
  large: 'min-h-[800px]',
  card: 'h-[400px]',
  carouselCard: 'h-[500px]',
  patologieCard: 'h-64',
  imageAspect: 'aspect-[3/4]',
  videoAspect: 'aspect-[9/16]'
};

const HeroSection = () => {
  return (
    <section className={`relative ${SECTION_HEIGHTS.hero} flex items-center`}>
      {/* Blur Gradient Background */}
      <BlurGradientWrapper 
        colors={["#ede6d4","#fffce4","#d2a750","#d2a750"]} 
        loop={true} 
      /> 
      
      {/* Mobile background overlay */}
      <div className="absolute inset-0 md:hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-beige-light/50 to-beige-light/90"></div>
      </div>
      
      <div className="container relative z-10 mx-auto px-4 flex flex-col md:flex-row items-center">
        {/* Mobile layout */}
        <div className="md:hidden flex flex-col items-center w-full">
          <div className="mb-6 w-3/4 max-w-[250px]">
            <div className={`bg-beige-dark rounded-30 ${SECTION_HEIGHTS.imageAspect} w-full overflow-hidden`}>
              <img 
                src="/images/AlessandraHomescreen_2.webp" 
                alt="Fotografia della dottoressa Alessandra Filzoli" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <div className="p-4 w-full">
            <h1 className="h1 text-green mb-4 text-center">
              Sblocca il potenziale della tua energia vitale
            </h1>
            <p className="body text-text-dark mb-8 text-center">
              Vivi uno stato di benessere e una vita gioiosa ed appagante grazie alla medicina integrata.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <ScrollToSectionButton 
                targetId="integrated-medicine-services"
                className="button bg-beige text-beige-dark px-10 py-5 rounded-15 hover:bg-accent hover:text-beige-light transition-colors w-full"
              >
                Leggi i benefici
              </ScrollToSectionButton>
              <Link to="/prenota" className="w-full">
                <button className="button bg-green text-beige-light px-10 py-5 rounded-15 font-semibold hover:bg-accent transition-colors w-full">
                  Prenota una visita
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Desktop/Tablet layout */}
        <div className="hidden md:flex md:w-1/2 md:pr-8">
          <div>
            <h1 className="h1 text-green mb-4">
              Sblocca il potenziale della tua energia vitale
            </h1>
            <p className="body text-text-dark mb-8">
              Vivi uno stato di benessere e una vita gioiosa ed appagante grazie alla medicina integrata.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <ScrollToSectionButton 
                targetId="integrated-medicine-services"
                className="button bg-beige text-beige-dark px-10 py-5 rounded-15 hover:bg-accent hover:text-beige-light transition-colors"
              >
                Leggi i benefici
              </ScrollToSectionButton>
              <Link to="/prenota">
                <button className="button bg-green text-beige-light px-10 py-5 rounded-15 font-semibold hover:bg-accent transition-colors">
                  Prenota una visita
                </button>
              </Link>
            </div>
          </div>
        </div>
        <div className="hidden md:block md:w-1/2 mt-8 md:mt-0">
          <div className={`bg-beige-dark rounded-30 ${SECTION_HEIGHTS.imageAspect} w-full max-w-md mx-auto overflow-hidden`}>
            <img 
              src="/images/AlessandraHomescreen_2.webp" 
              alt="Fotografia della dottoressa Alessandra Filzoli in un ambiente casalingo" 
              className="w-full h-full object-cover rounded-30"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const IntegratedMedicineSection = () => {
  return (
    <section className={`${SECTION_HEIGHTS.standard} flex items-center bg-green text-white py-20`}>
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row items-center">
          <div className="w-full sm:w-3/5 sm:pr-8 mb-8 sm:mb-0">
            <h2 className="h2 text-text-light mb-4">
              La Medicina Integrata:
            </h2>
            <h3 className="h3 text-text-light mb-4">
              Un Approccio Completo per la Tua Salute
            </h3>
            <p className="body text-text-light mb-4">
              Con oltre 30 anni di esperienza in medicina integrata, ti guido
              nel migliorare la tua salute a 360 gradi, attraverso i più
              recenti progressi della medicina convenzionale e tecniche di
              medicina cinese, agopuntura, yoga e meditazione
              (introduttiva ed avanzata) per favorire l'equilibrio energetico,
              ridurre il dolore e promuovere il benessere mentale, fisico ed
              emotivo.
            </p>
          </div>
          <div className="w-full sm:w-2/5">
            <div className={`rounded-30 ${SECTION_HEIGHTS.videoAspect} w-full max-w-md mx-auto scale-90 relative overflow-hidden`}>
              <ReactPlayer
                url="/videos/homepage/presentazione_alessandra.mp4"
                width="100%"
                height="100%"
                controls={true}
                light="/videos/homepage/presentazione_alessandra_cover.webp"
                playing={false}
                pip={true}
                stopOnUnmount={false}
                config={{
                  file: {
                    attributes: {
                      controlsList: 'nodownload'
                    },
                    forceVideo: true,
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ title, description, iconPath, buttonText, buttonLink, colorClass, highlightColor }) => (
  <div 
    className={`group ${colorClass} border-4 rounded-30 p-6 flex flex-col items-center text-white ${SECTION_HEIGHTS.card} transition-all duration-300 ease-in-out transform hover:scale-[1.02] hover:shadow-lg`}
    style={{
      '--highlight-color': highlightColor || '#FF9F1C',
    }}
  >
    <div 
      className={`w-16 h-16 rounded-full mb-4 flex items-center justify-center transition-all duration-300 ease-in-out bg-beige group-hover:bg-[var(--highlight-color)]`}
    >
      <img 
        src={iconPath}
        alt={title}
        className="w-9 h-9"
      />
    </div>
    <h4 className="h4 text-text-light mb-2 text-center">{title}</h4>
    {description ? (
      <p className="body_tile text-text-light mb-4 text-center flex-grow">{description}</p>
    ) : (
      <div className="flex-grow"></div>
    )}
    {buttonText && (
      buttonLink ? (
        <a 
          href={buttonLink} 
          className="button bg-accent text-text-light px-8 py-5 rounded-15 hover:bg-orange-500 transition-colors w-full mt-auto text-center inline-block"
        >
          {buttonText}
        </a>
      ) : (
        <button className="button bg-accent text-text-light px-8 py-5 rounded-15 hover:bg-orange-500 transition-colors w-full mt-auto">
          {buttonText}
        </button>
      )
    )}
  </div>
);

const IntegratedMedicineServices = () => {
  const services = [
    {
      title: "Medicina integrata",
      description: "Unisco la medicina tradizionale occidentale con trattamenti innovativi per fornire un approccio olistico alla cura della salute.",
      iconPath: "/icons/medicina_integrata.svg",
      colorClass: "bg-green",
      highlightColor: "#FF9F1C"
    },
    {
      title: "Medicina tradizionale, cinese e agopuntura",
      description: "Terapie che promuovono il rilassamento, alleviano il dolore e riequilibrano il sistema fisico-emotivo-energetico-mentale e spirituale.",
      iconPath: "/icons/agopuntura.svg",
      colorClass: "bg-green",
      highlightColor: "#FF9F1C"
    },
    {
      title: "Rimedi naturali e integratori",
      description: "Uso di rimedi naturali ed erboristici per trattare e prevenire le malattie.",
      iconPath: "/icons/rimedi_naturali.svg",
      colorClass: "bg-green",
      highlightColor: "#FF9F1C"
    },
    {
      title: "Medicina dello Stile di Vita",
      description: "Ti aiuto a prendere in mano la tua salute attraverso cambiamenti nelle abitudini quotidiane.",
      iconPath: "/icons/stile_di_vita.svg",
      colorClass: "bg-green",
      highlightColor: "#FF9F1C"
    },
    {
      title: "Tecniche di meditazione avanzata rivolte a tutti",
      description: "Impara tecniche di meditazione avanzate per migliorare il benessere mentale e fisico.",
      iconPath: "/icons/meditazione.svg",
      colorClass: "bg-green",
      highlightColor: "#FF9F1C"
    },
    {
      title: "Leggi di più sulla nostra wiki",
      buttonText: "Vai alla wiki!",
      buttonLink: "https://wiki.alessandrafilzoli.com",
      iconPath: "/icons/book.svg",
      colorClass: "bg-green-dark",
      highlightColor: "#FF9F1C"
    },
  ];

  return (
    <section className="bg-white py-20 px-4">
      <div className="container mx-auto">
        <h2 className="h2 text-text-dark mb-10 text-center">
          Le mie proposte di Medicina Integrata
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </div>
      </div>
    </section>
  );
};

const carouselItems = [
  {
    title: 'Nutrizione e abitudini alimentari',
    description: 'Scopri come alimentarti in modo consapevole e sano per prevenire e gestire i disagi e le malattie.',
    image: '/images/trattamenti/600/nutrizione.webp',
  },
  {
    title: 'Esercizio fisico e livello di attività',
    description: 'Implementa un piano di attività fisica che si adatti alle tue esigenze e obiettivi.',
    image: '/images/trattamenti/600/attivita_fisica.webp',
  },
  {
    title: 'Abitudini del sonno',
    description: 'Apprendi come migliorare la qualità del tuo sonno per un benessere generale.',
    image: '/images/trattamenti/600/abitudini_sonno.webp',
  },
  {
    title: 'Gestione dello stress',
    description: ' Impara tecniche efficaci per gestire lo stress e migliorare la tua qualità di vita.',
    image: '/images/trattamenti/600/stress.webp',
  },
  {
    title: 'Integrazione e supplementazione',
    description: 'Scopri quali integratori possono supportare la tua salute.',
    image: '/images/trattamenti/600/integratori.webp',
  },
];

const ChevronIcon = ({ direction, size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`transform ${direction === 'left' ? 'rotate-180' : ''}`}
  >
    <path
      d="M9 18L15 12L9 6"
      stroke="#2f4522"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowContainer = ({ onClick, direction, size = 24 }) => (
  <div
    className={`absolute top-1/2 transform -translate-y-1/2 cursor-pointer z-10 ${
      direction === 'left' ? '-left-12' : '-right-12'
    } hidden md:block`}
    onClick={onClick}
    style={{ width: `${size}px`, height: `${size}px` }}
  >
    <ChevronIcon direction={direction} size={size} />
  </div>
);

const PrevArrow = (props) => <ArrowContainer {...props} direction="left" size={60} />;
const NextArrow = (props) => <ArrowContainer {...props} direction="right" size={60} />;

const CarouselItem = ({ item }) => (
  <div className="px-4 py-2">
    <div className={`${SECTION_HEIGHTS.carouselCard} bg-beige rounded-15 overflow-hidden shadow-md transition-all duration-300 ease-in-out hover:shadow-lg hover:-translate-y-1 hover:bg-beige-light flex flex-col`}>
      <img src={item.image} alt={item.title} className="w-full h-[220px] object-cover" />
      <div className="p-6 flex flex-col flex-grow">
        <h4 className="h4 text-text-dark mb-2 text-center">{item.title}</h4>
        <p className="body_tile text-text-dark text-center flex-grow">{item.description}</p>
      </div>
    </div>
  </div>
);

const InfiniteCarousel = () => {
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 10000,
    cssEase: "ease",
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    swipeToSlide: true,
    touchThreshold: 5,
    useCSS: true,
    useTransform: true,
    responsive: [
      {
        breakpoint: 1500,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  return (
    <section className={`w-full ${SECTION_HEIGHTS.large} flex items-center justify-center bg-white text-dark-green p-4`}>
      <div className="w-full max-w-[1700px] mx-auto px-4 py-12 relative">
        <h2 className="h2 text-text-dark mb-10 text-center">Ti guiderò attraverso:</h2>
        <Slider {...settings}>
          {carouselItems.map((item, index) => (
            <div key={index} className="mx-2">
              <CarouselItem item={item} />
            </div>
          ))}
        </Slider>
      </div>  
    </section>
  );
};

const PatologieItems = [
  {
    title: 'Disturbi gastrointestinali',
    description: "Sindrome dell'intestino irritabile, reflusso gastroesofageo, disbiosi intestinale (stitichezza, diarrea) , malattia di Crohn.",
    image: '/images/patologie/600/maldipancia.webp',
  },
  {
    title: 'Problemi metabolici',
    description: ' Diabete di tipo 2, sindrome metabolica, ipercolesterolemia, sovrappeso.',
    image: '/images/patologie/600/metabolismo.webp',
  },
  {
    title: 'Disturbi del sonno',
    description: 'Insonnia, apnee notturne, disturbi del ritmo circadiano.',
    image: '/images/patologie/600/insonnia.webp',
  },
  {
    title: 'Problemi cardiovascolari',
    description: 'Ipertensione, aritmie, insufficienza cardiaca, prevenzione delle malattie cardiovascolari.',
    image: '/images/patologie/600/cuore.webp',
  },
  {
    title: 'Disturbi muscoloscheletrici',
    description: 'Dolore cronico, fibromialgia, osteoartrite, lombalgia, cervicalgia, sciatalgia, la spalla dolorosa.',
    image: '/images/patologie/600/dolori.webp',
  },
  {
    title: 'Malattie neurologiche e della sfera psichica',
    description: 'cefalea, emicrania, nevralgia del nervo faciale e del trigemino, herpes zoster, vertigini.',
    image: '/images/patologie/600/malditesta.webp',
  },
  {
    title: 'Malattie autoimmuni',
    description: 'Artrite reumatoide, lupus, sclerosi multipla, tiroidite di Hashimoto',
    image: '/images/patologie/600/autoimmune.webp',
  },
  {
    title: "Disturbi d'ansia e del tono dell'umore",
    description: 'Ansia, depressione, attacchi di panico, instabilità emotiva , gestione dello stress.',
    image: '/images/patologie/600/ansia.webp',
  },
  {
    title: 'Squilibri ormonali',
    description: "Sindrome premestruale (PMS), menopausa, ipo-ipertiroidismo, sindrome dell'ovaio policistico (PCOS)",
    image: '/images/patologie/600/ormonali.webp',
  },
];

const FadeCard = ({ item }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className="relative cursor-pointer mx-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`w-full ${SECTION_HEIGHTS.patologieCard} transition-opacity duration-300 rounded-lg overflow-hidden`}>
        {/* Front of the card */}
        <div className={`absolute w-full h-full transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
          <div 
            className="w-full h-full overflow-hidden shadow-md rounded-15"
            style={{
              backgroundImage: `url(${item.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center rounded-15 justify-center p-4">
              <h4 className="h4 text-text-light text-center">{item.title}</h4>
            </div>
          </div>
        </div>

        {/* Back of the card */}
        <div className={`absolute w-full h-full bg-green-dark rounded-15 p-4 flex items-center justify-center transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <p className="body_tile text-text-light text-center">{item.description}</p>
        </div>
      </div>
    </div>
  );
};

const PatologieCarousel = () => {
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 10000,
    cssEase: "ease",
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    swipeToSlide: true,
    touchThreshold: 5,
    useCSS: true,
    useTransform: true,
    responsive: [
      {
        breakpoint: 1500,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };
  
  return (
    <section className={`${SECTION_HEIGHTS.extended} flex flex-col items-center bg-green text-dark-green p-4`}>
      <div className="w-full max-w-6xl mx-auto mt-20 px-4 py-12">
        <h2 className="h2 text-text-light mb-5">Le principali patologie che tratto:</h2>
        <h3 className="h3 text-text-light">Per quale ragione si rivolgono a me i miei clienti?:</h3>
      </div>
      <div className="w-full max-w-[1700px] mx-auto px-4 mb-4">
        <Slider {...settings}>
          {PatologieItems.map((item, index) => (
            <FadeCard key={index} item={item} />
          ))}
        </Slider>
      </div>
    </section>
  );
};

const EnhancedEbookPromoSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    const section = document.getElementById('ebook-promo-section');
    if (section) observer.observe(section);
    
    return () => {
      if (section) observer.disconnect();
    };
  }, []);
  
  const benefits = [
    "Ricette semplici e gustose",
    "Consigli pratici per depurarti",
    "Tecniche di alimentazione consapevole",
    "Rituali di purificazione quotidiana"
  ];

  return (
    <section 
      id="ebook-promo-section"
      className="relative py-20 overflow-hidden bg-gradient-to-br from-beige-light to-beige"
    >
      {/* Background decoration */}
      <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-green/10 blur-3xl"></div>
      <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-accent/10 blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-5xl mx-auto"
        >
          
          <div className="flex flex-col lg:flex-row items-center gap-6 md:gap-8 lg:gap-12">
            {/* Left side - Image with consistent height */}
            <motion.div 
              className="lg:w-1/2"
              initial={{ x: -50 }}
              animate={isVisible ? { x: 0 } : { x: -50 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-green/20 rounded-30 blur-xl transform scale-90"></div>
                
                <div className="relative rounded-30 overflow-hidden shadow-xl transform transition-all duration-500 hover:scale-[1.03] hover:shadow-2xl max-w-md mx-auto">
                  <div className={`${SECTION_HEIGHTS.imageAspect} max-h-[70vh]`}>
                    <img 
                      src="/images/products/il-cibo-che-purifica/copertina.webp" 
                      alt="Copertina del corso Il Cibo che Purifica con la Dott.ssa Alessandra Filzoli"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="absolute bottom-4 right-4 bg-white/90 p-3 rounded-full shadow-md transform transition-transform hover:scale-110">
                    <PlayCircle className="text-green" size={32} />
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Right side - Content */}
            <motion.div 
              className="lg:w-1/2"
              initial={{ x: 50 }}
              animate={isVisible ? { x: 0 } : { x: 50 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="flex items-center">
                <span className="text-3xl mr-3 text-green">🌿</span>
                <h3 className="h3 text-green">
                  E-book con video-ricette
                </h3>
              </div>
              
              <p className="body text-text-dark mb-8">
                Costruisci oggi le basi per un corpo più leggero, una mente più lucida e un'energia più viva. 
                Un percorso completo che unisce teoria e pratica per un benessere duraturo.
              </p>
              
              <div className="mb-8 bg-white p-6 rounded-15 shadow-md">
                <h4 className="h4 text-green mb-4">Cosa include:</h4>
                <div className="grid grid-cols-1 gap-3">
                  {benefits.map((benefit, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ duration: 0.3, delay: 0.6 + (index * 0.1) }}
                      className="flex items-start"
                    >
                      <div className="mr-3 pt-1 text-green">
                        <Check size={20} />
                      </div>
                      <p className="body-tile text-text-dark">{benefit}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 1 }}
              >
                <Link to="/il-cibo-che-purifica">
                  <button className="flex h4 items-center justify-center px-8 py-5 w-full bg-green text-beige-light font-bold rounded-full hover:bg-accent transition-all duration-300 shadow-md hover:shadow-lg">
                    Scopri tutti i dettagli
                    <ChevronRight className="ml-2" size={20} />
                  </button>
                </Link>
                
                <p className="text-center text-sm text-gray-500 mt-4">
                  E-Book completo + Videoricette pratiche - Accesso permanente
                </p>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const NewsletterSubscription = () => {
  const handleSubmissionComplete = (result) => {
    console.log('Newsletter submission completed:', result);
  };

  return (
    <section className={`${SECTION_HEIGHTS.extended} flex items-center justify-center bg-green text-dark-green p-4`}>
      <div className="max-w-4xl w-full px-4 sm:px-6 lg:px-8">
        <h2 className="h2 text-white text-center">Ricevi Consigli e Aggiornamenti</h2>
        <p className="body text-white mb-8 text-center">Iscriviti alla mia newsletter per rimanere sempre in contatto!</p>
        
        <div className="flex justify-center mb-8">
          <div id="spiegazione" className="bg-beige p-8 rounded-15 shadow-lg">
            <NewsletterForm 
              onSubmissionComplete={handleSubmissionComplete}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const CenteredDivider = ({ height = '3px' }) => {
  return (
    <div className="flex justify-center w-full my-8">
      <div 
        className="w-full max-w-[1200px] bg-green-dark rounded-full" 
        style={{ height }}
      />
    </div>
  );
};

const Home = () => {
  return (
    <div className="flex flex-col">
      <HeroSection />
      <IntegratedMedicineSection />
      <div id="integrated-medicine-services">
        <IntegratedMedicineServices />
      </div>
      <CenteredDivider />
      <InfiniteCarousel />
      <PatologieCarousel />
      <EnhancedEbookPromoSection/>
      <NewsletterSubscription />
    </div>
  );
};

export default Home;